import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Globe, TrendingUp, Wallet, Users, MapPin, AlertTriangle, CheckCircle, Download, RefreshCw, Target, Leaf, Heart } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import BackButton from '../components/BackButton';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function CoppamGroupDashboard() {
  const [territories, setTerritories] = useState([]);
  const [funds, setFunds] = useState([]);
  const [fundRatios, setFundRatios] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [territoriesRes, fundsRes, ratiosRes] = await Promise.all([
        axios.get(`${API_URL}/api/territories`),
        axios.get(`${API_URL}/api/funds`),
        axios.get(`${API_URL}/api/funds/ratios`)
      ]);
      
      setTerritories(territoriesRes.data.territories || []);
      setFunds(fundsRes.data.funds || []);
      setFundRatios(ratiosRes.data);
    } catch {
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const getTerritoryFlag = (code) => {
    const flags = {
      'GP': '🇬🇵', 'MQ': '🇲🇶', 'GF': '🇬🇫', 'RE': '🇷🇪',
      'YT': '🇾🇹', 'MF': '🇲🇫', 'BL': '🇧🇱'
    };
    return flags[code] || '🏳️';
  };

  const getCoverageColor = (taux) => {
    if (taux < 30) return 'text-red-600 bg-red-50';
    if (taux < 50) return 'text-amber-600 bg-amber-50';
    return 'text-green-600 bg-green-50';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <RefreshCw className="h-8 w-8 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Chargement du tableau de bord...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-800">
      {/* Header */}
      <div className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <BackButton className="text-white" />
              <div>
                <h1 className="text-2xl font-bold text-white flex items-center gap-2">
                  <Globe className="h-6 w-6" />
                  COPPAM - Dashboard Groupe
                </h1>
                <p className="text-blue-200 text-sm">Vue consolidée multi-territoires DOM</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <Button variant="outline" className="border-white/30 text-white hover:bg-white/10">
                <Download className="h-4 w-4 mr-2" />
                Exporter
              </Button>
              <Button onClick={loadData} variant="outline" className="border-white/30 text-white hover:bg-white/10">
                <RefreshCw className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* KPIs globaux */}
        {fundRatios && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <Card className="bg-white/10 border-white/20 text-white">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-blue-500/20 rounded-xl">
                    <Wallet className="h-6 w-6 text-blue-400" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold">
                      {(fundRatios.total_dotation / 1000000).toFixed(2)}M €
                    </div>
                    <div className="text-xs text-blue-200">Dotation totale</div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white/10 border-white/20 text-white">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-amber-500/20 rounded-xl">
                    <TrendingUp className="h-6 w-6 text-amber-400" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold">
                      {(fundRatios.total_engagements / 1000).toFixed(0)}k €
                    </div>
                    <div className="text-xs text-blue-200">Engagements</div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white/10 border-white/20 text-white">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-green-500/20 rounded-xl">
                    <CheckCircle className="h-6 w-6 text-green-400" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold">
                      {(fundRatios.total_disponible / 1000000).toFixed(2)}M €
                    </div>
                    <div className="text-xs text-blue-200">Disponible</div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white/10 border-white/20 text-white">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-purple-500/20 rounded-xl">
                    <Target className="h-6 w-6 text-purple-400" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold">
                      {fundRatios.taux_couverture_global.toFixed(1)}%
                    </div>
                    <div className="text-xs text-blue-200">Couverture globale</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Carte des territoires */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {/* Liste des territoires */}
          <div className="lg:col-span-2">
            <Card className="bg-white">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MapPin className="h-5 w-5 text-blue-600" />
                  Territoires DOM
                </CardTitle>
                <CardDescription>
                  7 territoires pilot activés
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {funds.map((fund) => {
                    const territory = territories.find(t => t.code === fund.territory_code);
                    return (
                      <div 
                        key={fund.territory_code}
                        className="p-4 border rounded-xl hover:shadow-lg transition-all"
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <span className="text-2xl">{getTerritoryFlag(fund.territory_code)}</span>
                            <div>
                              <div className="font-semibold">{fund.territory_name}</div>
                              <div className="text-xs text-gray-500">{territory?.region}</div>
                            </div>
                          </div>
                          {fund.alerte === 'critique' && (
                            <Badge className="bg-red-500 text-white text-xs">⚠️</Badge>
                          )}
                          {fund.alerte === 'attention' && (
                            <Badge className="bg-amber-500 text-white text-xs">⚠️</Badge>
                          )}
                          {!fund.alerte && (
                            <Badge className="bg-green-500 text-white text-xs">✅</Badge>
                          )}
                        </div>
                        
                        <div className="grid grid-cols-3 gap-2 text-center text-sm">
                          <div className="p-2 bg-blue-50 rounded">
                            <div className="font-semibold text-blue-600">
                              {(fund.dotation / 1000).toFixed(0)}k
                            </div>
                            <div className="text-xs text-gray-500">Dotation</div>
                          </div>
                          <div className="p-2 bg-amber-50 rounded">
                            <div className="font-semibold text-amber-600">
                              {(fund.engagements / 1000).toFixed(0)}k
                            </div>
                            <div className="text-xs text-gray-500">Engag.</div>
                          </div>
                          <div className={`p-2 rounded ${getCoverageColor(fund.taux_couverture)}`}>
                            <div className="font-semibold">
                              {fund.taux_couverture.toFixed(0)}%
                            </div>
                            <div className="text-xs opacity-75">Couv.</div>
                          </div>
                        </div>
                        
                        {/* Barre de progression */}
                        <div className="mt-3 h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div 
                            className={`h-full ${
                              fund.taux_couverture < 30 ? 'bg-red-500' :
                              fund.taux_couverture < 50 ? 'bg-amber-500' : 'bg-green-500'
                            }`}
                            style={{ width: `${Math.min(fund.taux_couverture, 100)}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Panel latéral */}
          <div className="space-y-6">
            {/* Alertes */}
            {fundRatios && (fundRatios.territories_critiques.length > 0 || fundRatios.territories_en_alerte.length > 0) && (
              <Card className="bg-white border-amber-300">
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2 text-amber-700">
                    <AlertTriangle className="h-5 w-5" />
                    Alertes actives
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {fundRatios.territories_critiques.map((code) => {
                    const t = territories.find(t => t.code === code);
                    return (
                      <div key={code} className="flex items-center gap-2 p-2 bg-red-50 rounded text-red-700">
                        <span className="text-lg">{getTerritoryFlag(code)}</span>
                        <span className="font-semibold">{t?.name || code}</span>
                        <Badge className="bg-red-500 text-white ml-auto">Critique</Badge>
                      </div>
                    );
                  })}
                  {fundRatios.territories_en_alerte.map((code) => {
                    const t = territories.find(t => t.code === code);
                    return (
                      <div key={code} className="flex items-center gap-2 p-2 bg-amber-50 rounded text-amber-700">
                        <span className="text-lg">{getTerritoryFlag(code)}</span>
                        <span className="font-semibold">{t?.name || code}</span>
                        <Badge className="bg-amber-500 text-white ml-auto">Attention</Badge>
                      </div>
                    );
                  })}
                </CardContent>
              </Card>
            )}

            {/* Indicateurs ESS */}
            <Card className="bg-white">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Leaf className="h-5 w-5 text-green-600" />
                  Impact ESS
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-green-50 rounded-xl">
                  <div className="flex items-center gap-2">
                    <Users className="h-5 w-5 text-green-600" />
                    <span className="text-sm">Adhérents actifs</span>
                  </div>
                  <span className="font-bold text-green-600">-</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-blue-50 rounded-xl">
                  <div className="flex items-center gap-2">
                    <Heart className="h-5 w-5 text-blue-600" />
                    <span className="text-sm">Entraide versée</span>
                  </div>
                  <span className="font-bold text-blue-600">-</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-purple-50 rounded-xl">
                  <div className="flex items-center gap-2">
                    <Target className="h-5 w-5 text-purple-600" />
                    <span className="text-sm">ODD alignés</span>
                  </div>
                  <span className="font-bold text-purple-600">1, 8, 10, 11</span>
                </div>
              </CardContent>
            </Card>

            {/* Mention légale */}
            <div className="p-4 bg-white/10 rounded-xl text-white/70 text-xs">
              <strong className="text-white">Dispositif ESS</strong>
              <p className="mt-1">
                Fondé sur le droit civil français et les principes de l'Économie Sociale et Solidaire.
                Hors assurance – aucune prime – aucune garantie automatique.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
